'use client';

/**
 * CardGrid - Unified card grid rendering
 * 
 * Renders a grid of cards with configurable columns and styling.
 */

import React from 'react';
import { CardGridConfig, AnimationConfig } from '../types';
import { SmoothReveal } from '@/components/shared/SmoothReveal';
import { SMOOTH_IN_VIEW_STAGGER } from '@/hooks/useSmoothInViewAnimation';
import { getBrandColor } from '@/config/brand-colors';
import S3FilePreview from '@/components/shared/S3FilePreview';

interface CardGridProps {
  config?: CardGridConfig;
  animation?: AnimationConfig;
  className?: string;
}

export function CardGrid({ config, animation, className = '' }: CardGridProps) {
  if (!config || !config.cards || config.cards.length === 0) return null;

  const { cards, columns = 3, gap = '6', cardStyle = 'default' } = config;

  // Determine grid columns
  const getColumnsClass = () => {
    if (typeof columns === 'number') {
      const colMap: Record<number, string> = {
        1: 'grid-cols-1',
        2: 'grid-cols-1 sm:grid-cols-2',
        3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
        4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
      };
      return colMap[columns] || colMap[3];
    }
    return `grid-cols-${columns.mobile} sm:grid-cols-${columns.tablet} lg:grid-cols-${columns.desktop}`;
  };

  const cardStyleClasses: Record<string, string> = {
    default: 'bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1',
    bordered: 'bg-white rounded-2xl border border-gray-200 hover:border-gray-300 transition-all duration-300',
    elevated: 'bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2',
    minimal: 'bg-gray-50 rounded-xl hover:bg-gray-100 transition-all duration-300',
  };

  const delay = animation?.delay || 0;
  const stagger = animation?.stagger || SMOOTH_IN_VIEW_STAGGER;

  return (
    <div className={`grid ${getColumnsClass()} gap-${gap} ${className}`}>
      {cards.map((card, index) => (
        <SmoothReveal
          key={card.id || index}
          delay={delay + index * stagger}
          className={cardStyleClasses[cardStyle]}
        >
          <div className="overflow-hidden rounded-2xl h-full flex flex-col">
            {/* Card Image */}
            {card.image && (
              <div className="relative aspect-[4/3] overflow-hidden">
                <S3FilePreview
                  src={card.image}
                  alt={card.title || 'Card image'}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  disablePreviewModal={false}
                  disableSkeleton={false}
                />
              </div>
            )}

            {/* Card Content */}
            <div className="p-6 flex-1 flex flex-col">
              {/* Badge */}
              {card.badge && (
                <span 
                  className="inline-block px-3 py-1 rounded-full text-xs font-medium mb-3 self-start"
                  style={{
                    backgroundColor: getBrandColor('accentOrange') + '20',
                    color: getBrandColor('accentOrange'),
                  }}
                >
                  {card.badge}
                </span>
              )}

              {/* Title */}
              {card.title && (
                <h3 
                  className="text-xl font-bold mb-2"
                  style={{ color: getBrandColor('deepNavy') }}
                >
                  {card.title}
                </h3>
              )}

              {/* Description */}
              {card.description && (
                <p 
                  className="text-sm leading-relaxed flex-1"
                  style={{ color: '#666' }}
                >
                  {card.description}
                </p>
              )}

              {/* Link */}
              {card.link && (
                <a 
                  href={card.link}
                  className="mt-4 text-sm font-medium inline-flex items-center gap-1 hover:gap-2 transition-all"
                  style={{ color: getBrandColor('primaryBlue') }}
                >
                  Learn more
                  <span>→</span>
                </a>
              )}
            </div>
          </div>
        </SmoothReveal>
      ))}
    </div>
  );
}

export default CardGrid;
